/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Input } from 'antd';
import { Rule, RuleObject } from 'antd/lib/form';
import { InputProps } from 'antd/lib/input';
import React, { useContext, useEffect, useRef, useState } from 'react';

import Paragraph from 'components/atoms/Paragraph';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import InfoTooltip from 'components/molecules/InfoTooltip';

import { formatMoneyAmount } from 'enums/regExp';

import getLanguageObject from 'utils/getLanguageObject';

const { Item } = Form;

interface IInputNumber extends InputProps {
    label?: string;
    name: string;
    initialValue?: string | number;
    rules?: Rule[];
    extramessage?: string;
    notRequired?: boolean;
    precision?: number;
    disabled?: boolean;
    horizontalLayout?: boolean;
    onChangeCustom?: (value: string | number | undefined) => void;
    formatter?: (value: number | string | undefined) => string;
    infoTooltipText?: string;
    style?: React.CSSProperties;
    id?: string;
}

const InputNumberComponent: React.FC<IInputNumber> = props => {
    const defaultRules = [
        () => ({
            validator(_: RuleObject, fValue: number) {
                // const re = /^\d+(\.\d{1,2})?$/;
                if (!fValue || !isNaN(fValue)) return Promise.resolve();
                return Promise.reject(<TranslateText>NEVALIDNI_NUM</TranslateText>);
            }
        })
    ];
    const { language } = useContext(Context);
    const {
        label,
        name,
        initialValue,
        precision = 2,
        rules = defaultRules,
        notRequired,
        disabled,
        horizontalLayout,
        extramessage = '',
        onChangeCustom,
        infoTooltipText = '',
        style,
        id,
        maxLength,
        ...rest
    } = props;

    const [formValue, setFormValue] = useState<number | string | undefined>(initialValue); // hodnota drzana v FormWrapper
    const [inputValue, setInputValue] = useState<number | string | undefined>(initialValue); // hodnota zobrazeneho inputu
    const [fieldTouched, setFieldtouched] = useState<boolean>(false); // zabrani validacii pri prvom rendri
    const [showError, setShowError] = useState<boolean>(false); // hlada antd item error class a nastavi farbicky rendrovanemu inputu
    const formInputNumberRef = useRef<any>(null);
    const WrapperRef = useRef<any>(null);

    useEffect(() => {
        /* spusti onclick event na form iteme a vdaka getValueFromEvent updatne hodnotu */
        const numFormInput: any = formInputNumberRef.current.nativeElement;
        const newEv = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true
        });
        numFormInput.dispatchEvent(newEv);
        /* ============================================================================ */
        setTimeout(() => {
            !fieldTouched && setFieldtouched(true);
            // nastavenie showError
            const formItemDiv: any = WrapperRef.current?.nativeElement;
            if (!numFormInput) return;
            setShowError(
                formItemDiv?.children &&
                    formItemDiv?.children.length &&
                    formItemDiv?.children[0].className.includes('error')
            );
        }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue]);

    const formatToNum = (val: number | string, isOnChangeEvent?: boolean) => {
        // formatovanie z 0.01 na 0,01 v zobrazenom inpute
        const newVal =
            val !== undefined && val !== null && val !== '' ? val.toString().replace('.', ',').split(' ').join('') : '';
        const parts = newVal.toString().split(',');
        parts[0] = parts[0].substring(0, maxLength).replace(formatMoneyAmount, ' '); // formatovanie 1000 => 1 000
        parts[1] && parts[1].length ? (parts[1] = parts[1].slice(0, precision)) : null; // zmaze 10tine miesta ak ich je viac ako urcene v precision prop
        const FormVal = parts.join('.').split(' ').join('');
        FormVal && setFieldtouched(true);
        setFormValue(!FormVal ? '' : isNaN(Number(FormVal)) ? FormVal : Number(FormVal));
        if (precision === 0 && parts.length > 1) parts.pop();
        setInputValue(parts.filter(e => !isNaN(Number(e.split(' ').join('')))).join(','));
        onChangeCustom &&
            isOnChangeEvent &&
            setTimeout(
                () => onChangeCustom(!FormVal ? undefined : isNaN(Number(FormVal)) ? FormVal : Number(FormVal)),
                0
            );
    };

    return (
        <div
            ref={WrapperRef}
            style={{
                height: horizontalLayout ? 55 : 87, // prisposobene vyske itemov.. ked tak fixnut nejako dynamicky
                display: 'block',
                marginBottom: horizontalLayout ? 0 : 12,
                ...style
            }}
        >
            <Item
                label={
                    label ? (
                        <>
                            <Paragraph text={label} strong={true} />{' '}
                            {infoTooltipText && <InfoTooltip title={infoTooltipText} />}{' '}
                        </>
                    ) : null
                }
                name={name}
                preserve={false}
                initialValue={initialValue || formValue}
                getValueFromEvent={ev => {
                    !fieldTouched && formatToNum(ev.target.value || initialValue);
                    return formValue;
                }}
                rules={
                    notRequired || !fieldTouched
                        ? rules
                        : [
                              {
                                  required: true,
                                  message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                              },
                              ...defaultRules,
                              ...rules
                          ]
                }
                trigger={'onClick'}
                validateTrigger={['onSubmit', fieldTouched ? 'onClick' : '']}
                shouldUpdate={(prevValue: any, curValue: any) => {
                    if (prevValue[name] !== curValue[name]) {
                        formatToNum(curValue[name]);
                        return false;
                    }
                    // zabudol som aky ma toto ucel ale vadilo to pri form.setfields zatial zakomentovane ked tak vratit
                    // musel som vratit lebo sa mazali hodnoty pri docasneho skryti pola co sa pri ostatnych komponentach nedeje form.setfields funguje zatial fajn
                    if (
                        curValue[name] !== undefined &&
                        inputValue &&
                        String(curValue[name] || '')
                            .split('.')
                            .filter((e: any) => e.length)
                            .join('.') !==
                            String(inputValue)
                                ?.split(',')
                                .filter(e => e.length)
                                .join('.')
                                .split(' ')
                                .join('')
                    ) {
                        inputValue && formatToNum(inputValue || '');
                        return false;
                    }
                    return false;
                }}
                extra={<Paragraph text={extramessage} className="extramessage-text" />}
                id={id || name}
            >
                {/* tento input je skryty ale updatujeme jeho value kvoli formWrapperu */}
                <Input
                    style={{
                        display: 'none'
                    }}
                    className="input-component"
                    ref={formInputNumberRef}
                    {...rest}
                />
            </Item>

            <Input
                style={
                    !horizontalLayout
                        ? {
                              position: 'relative',
                              width: '100%',
                              bottom: 68
                          }
                        : {
                              position: 'relative',
                              width: '75%',
                              top: -72,
                              right: '-25%'
                          }
                }
                disabled={disabled}
                placeholder={props.disabled ? '' : getLanguageObject(language).VYPLNTE_PLACEHOLDER || 'Vyplňte...'}
                className={showError ? 'inputNumberErr' : ''}
                value={inputValue}
                onChange={ev => {
                    formatToNum(ev.target.value, true);
                }}
                id={id || name}
                maxLength={(() => {
                    // osetrenie maxlength = potrebujem ignorovat naformatovane medzery a desatine cisla
                    const hasDecimal = String(inputValue).includes(',');
                    return (
                        (maxLength || 25) +
                        (inputValue ? String(inputValue).split(' ').length - 1 : 0) +
                        (hasDecimal ? precision + 1 : 1)
                    );
                })()}
                {...rest}
            />
        </div>
    );
};

export default InputNumberComponent;
